<template>
  <ul class="company-wall-products">
    <CompanyWallProductsItem
      v-for="parent in firstProducts"
      :key="parent.id"
      :customer-id="customerId"
      :parent="parent"
    />
    <SCollapse
      v-if="isMobile"
      class="company-wall-products__collapse"
      :expanded="showMore"
    >
      <div class="company-wall-products__more-items">
        <CompanyWallProductsItem
          v-for="parent in otherProducts"
          :key="parent.id"
          :customer-id="customerId"
          :parent="parent"
        />
      </div>
    </SCollapse>
    <template v-else>
      <CompanyWallProductsItem
        v-for="parent in otherProducts"
        :key="parent.id"
        :customer-id="customerId"
        :parent="parent"
      />
    </template>
    <SButton
      v-if="isMobile && otherProducts.length > 0"
      action-type="quaternary"
      class="company-wall-products__show-more"
      size="inline"
      @click="showMore = !showMore"
    >
      {{ showMoreLabel }}
    </SButton>
  </ul>
</template>

<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { computed, ref } from "vue";

import { SButton, SCollapse } from "@solvari/common-fe";
import { useI18nGeneral } from "@solvari/translations";

import CompanyWallProductsItem from "~/features/company-wall/components/company-wall-details/content/CompanyWallProductsItem.vue";

const props = defineProps<{
  customerId: number;
  geoProductCategories: App.Data.CompanyWall.CompanyWallProductWithEndProductsData[];
}>();

const { tr } = useI18nGeneral();
const { width } = useWindowSize();

const showMore = ref(false);
const isMobile = computed(() => width.value < 500);

const sortedGeoProductCategories = computed(() => {
  const seen = new Set<number>();
  return props.geoProductCategories.map((category) => {
    const active = category.endProducts.filter((ep) => ep.active);
    const endProducts = [
      ...active.filter((ep) => !seen.has(ep.id)),
      ...active.filter((ep) => seen.has(ep.id)),
    ];
    active.forEach((ep) => seen.add(ep.id));
    return { ...category, endProducts };
  });
});

const firstProducts = computed(() =>
  sortedGeoProductCategories.value.slice(0, 5),
);
const otherProducts = computed(() => sortedGeoProductCategories.value.slice(5));
const showMoreLabel = computed(() => {
  return showMore.value
    ? tr("general.show_less")
    : tr("general.show_more_amount", { amount: otherProducts.value.length });
});
</script>

<style lang="postcss">
.company-wall-products {
  @apply grid gap-2 grid-cols-auto-56 grid-cols-auto-grow;

  &__collapse {
    @apply col-span-full;
  }

  &__more-items {
    @apply grid px-px pt-px gap-2 grid-cols-auto-56 grid-cols-auto-grow;
  }

  &__show-more {
    @apply col-span-full mt-2 w-fit;
  }
}
</style>
